import {
	Children,
	Context,
	ElementValue,
	Portal,
	Renderer,
	RenderAdapter,
} from "./crank.js";
import {camelToKebabCase, formatStyleValue} from "./_css.js";
import {REACT_SVG_PROPS} from "./_svg.js";

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const MATHML_NAMESPACE = "http://www.w3.org/1998/Math/MathML";

function getRootDocument(root: Node | undefined): Document {
	if (root && root.ownerDocument) {
		return root.ownerDocument;
	}

	if (root && root.nodeType === Node.DOCUMENT_NODE) {
		return root as unknown as Document;
	}

	return document;
}

const writablePropertyCache = new WeakMap<Function, Map<string, boolean>>();

function isWritableProperty(element: Element, name: string): boolean {
	// Check own properties first — custom elements may define per-instance
	// writable properties via Object.defineProperty(this, ...).
	if (Object.prototype.hasOwnProperty.call(element, name)) {
		const descriptor = Object.getOwnPropertyDescriptor(element, name);
		return (
			descriptor != null &&
			(descriptor.writable === true || descriptor.set !== undefined)
		);
	}

	// For prototype-chain properties, use the cache keyed by constructor.
	const ctor = element.constructor;
	let ctorCache = writablePropertyCache.get(ctor);
	if (ctorCache !== undefined) {
		const cached = ctorCache.get(name);
		if (cached !== undefined) {
			return cached;
		}
	}

	// Walk up the prototype chain (skip the instance, already checked)
	let propOwner = Object.getPrototypeOf(element);
	while (propOwner !== null) {
		if (Object.prototype.hasOwnProperty.call(propOwner, name)) {
			break;
		}

		propOwner = Object.getPrototypeOf(propOwner);
	}

	let result = false;
	if (propOwner !== null) {
		const descriptor = Object.getOwnPropertyDescriptor(propOwner, name);
		result =
			descriptor != null &&
			(descriptor.writable === true || descriptor.set !== undefined);
	}

	if (ctorCache === undefined) {
		ctorCache = new Map();
		writablePropertyCache.set(ctor, ctorCache);
	}

	ctorCache.set(name, result);
	return result;
}

function emitHydrationWarning(
	propName: string,
	quietProps: Set<string> | undefined,
	expectedValue: any,
	actualValue: any,
	element: Element,
	displayName?: string,
) {
	const checkName = propName;
	const showName = displayName || propName;
	if (!quietProps || !quietProps.has(checkName)) {
		if (expectedValue === null || expectedValue === false) {
			console.warn(
				`Expected "${showName}" to be missing but found ${String(actualValue)} while hydrating:`,
				element,
			);
		} else if (expectedValue === true || expectedValue === "") {
			console.warn(
				`Expected "${showName}" to be ${expectedValue === true ? "present" : '""'} but found ${String(actualValue)} while hydrating:`,
				element,
			);
		} else {
			// Check if this is a URL mismatch that's actually just resolution
			const win = element.ownerDocument.defaultView;
			if (win && win.location) {
				const origin = win.location.origin;
				if (
					new URL(expectedValue, origin).href ===
					new URL(actualValue, origin).href
				) {
					// attrs which are URLs will often be resolved to their full
					// href in the DOM, so we squash these errors
					return;
				}
			}
			console.warn(
				`Expected "${showName}" to be "${String(expectedValue)}" but found ${String(actualValue)} while hydrating:`,
				element,
			);
		}
	}
}

function patchProp(
	element: Element,
	name: string,
	value: any,
	oldValue: any,
	props: Record<string, any>,
	isSVG: boolean,
	isMathML: boolean,
	copyProps: Set<string> | undefined,
	quietProps: Set<string> | undefined,
	isHydrating: boolean,
): void {
	if (copyProps != null && copyProps.has(name)) {
		return;
	}
	// handle prop:name or attr:name properties
	const colonIndex = name.indexOf(":");
	if (colonIndex !== -1) {
		const [ns, name1] = [name.slice(0, colonIndex), name.slice(colonIndex + 1)];
		switch (ns) {
			case "prop":
				(element as any)[name1] = value;
				return;
			case "attr":
				if (value == null || value === false) {
					if (isHydrating && element.hasAttribute(name1)) {
						emitHydrationWarning(
							name,
							quietProps,
							value,
							element.getAttribute(name1),
							element,
						);
					}
					element.removeAttribute(name1);
					return;
				} else if (value === true) {
					if (isHydrating && !element.hasAttribute(name1)) {
						emitHydrationWarning(name, quietProps, value, null, element);
					}
					element.setAttribute(name1, "");
					return;
				}

				if (typeof value !== "string") {
					value = String(value);
				}

				if (isHydrating && element.getAttribute(name1) !== value) {
					emitHydrationWarning(
						name,
						quietProps,
						value,
						element.getAttribute(name1),
						element,
					);
				}

				element.setAttribute(name1, value);
				return;
		}
	}

	switch (name) {
		// TODO: fix hydration warnings for the style prop
		case "style": {
			const style = (element as HTMLElement | SVGElement).style;
			if (value == null || value === false) {
				if (isHydrating && style.cssText !== "") {
					emitHydrationWarning(name, quietProps, value, style.cssText, element);
				}
				element.removeAttribute("style");
			} else if (value === true) {
				if (isHydrating && style.cssText !== "") {
					emitHydrationWarning(name, quietProps, "", style.cssText, element);
				}
				element.setAttribute("style", "");
			} else if (typeof value === "string") {
				if (style.cssText !== value) {
					// TODO: Fix hydration warnings for styles
					//if (isHydrating) {
					//	emitHydrationWarning(
					//		name,
					//		quietProps,
					//		value,
					//		style.cssText,
					//		element,
					//	);
					//}

					style.cssText = value;
				}
			} else {
				if (typeof oldValue === "string") {
					// if the old value was a string, we need to clear the style
					// TODO: only clear the styles enumerated in the old value
					style.cssText = "";
				}

				// First pass: remove styles present in oldValue but not in value
				if (oldValue) {
					for (const styleName in oldValue) {
						if (value && styleName in value) continue;
						const cssName = camelToKebabCase(styleName);
						if (isHydrating && style.getPropertyValue(cssName) !== "") {
							emitHydrationWarning(
								name,
								quietProps,
								null,
								style.getPropertyValue(cssName),
								element,
								`style.${styleName}`,
							);
						}
						style.removeProperty(cssName);
					}
				}
				// Second pass: apply all styles from value
				if (value) {
					for (const styleName in value) {
						const cssName = camelToKebabCase(styleName);
						const styleValue = (value as any)[styleName];
						if (styleValue == null) {
							if (isHydrating && style.getPropertyValue(cssName) !== "") {
								emitHydrationWarning(
									name,
									quietProps,
									null,
									style.getPropertyValue(cssName),
									element,
									`style.${styleName}`,
								);
							}
							style.removeProperty(cssName);
						} else {
							const formattedValue = formatStyleValue(cssName, styleValue);
							if (style.getPropertyValue(cssName) !== formattedValue) {
								// TODO: hydration warnings for style props
								//if (isHydrating) {
								//	emitHydrationWarning(
								//		name,
								//		quietProps,
								//		formattedValue,
								//		style.getPropertyValue(cssName),
								//		element,
								//		`style.${styleName}`,
								//	);
								//}
								style.setProperty(cssName, formattedValue);
							}
						}
					}
				}
			}

			break;
		}
		case "class":
		case "className":
			if (name === "className" && "class" in props) break;
			if (value === true) {
				if (isHydrating && element.getAttribute("class") !== "") {
					emitHydrationWarning(
						name,
						quietProps,
						"",
						element.getAttribute("class"),
						element,
					);
				}
				element.setAttribute("class", "");
			} else if (value == null) {
				if (isHydrating && element.hasAttribute("class")) {
					emitHydrationWarning(
						name,
						quietProps,
						value,
						element.getAttribute("class"),
						element,
					);
				}

				element.removeAttribute("class");
			} else if (typeof value === "object") {
				// class={{"included-class": true, "excluded-class": false}} syntax
				if (typeof oldValue === "string") {
					// if the old value was a string, we need to clear all classes
					element.setAttribute("class", "");
				}

				let shouldIssueWarning = false;
				const hydratingClasses = isHydrating
					? new Set(Array.from(element.classList))
					: undefined;
				const hydratingClassName = isHydrating
					? element.getAttribute("class")
					: undefined;

				// Two passes: removes first, then adds. This ensures that
				// overlapping classes in different keys are handled correctly.
				// e.g. {"a b": false, "b c": true} should result in "b c"
				// Remove pass: iterate oldValue for classes to remove
				if (oldValue) {
					for (const classNames in oldValue) {
						if (value && value[classNames]) continue;
						const classes = classNames.split(/\s+/).filter(Boolean);
						element.classList.remove(...classes);
					}
				}

				// Add pass: iterate value for classes to add
				if (value) {
					for (const classNames in value) {
						if (!value[classNames]) continue;
						const classes = classNames.split(/\s+/).filter(Boolean);
						element.classList.add(...classes);
						for (const className of classes) {
							if (hydratingClasses && hydratingClasses.has(className)) {
								hydratingClasses.delete(className);
							} else if (isHydrating) {
								shouldIssueWarning = true;
							}
						}
					}
				}

				if (
					shouldIssueWarning ||
					(hydratingClasses && hydratingClasses.size > 0)
				) {
					emitHydrationWarning(
						name,
						quietProps,
						Object.keys(value)
							.filter((k) => value[k])
							.join(" "),
						hydratingClassName || "",
						element,
					);
				}
			} else if (!isSVG && !isMathML) {
				if (element.className !== value) {
					if (isHydrating) {
						emitHydrationWarning(
							name,
							quietProps,
							value,
							element.className,
							element,
						);
					}
					element.className = value;
				}
			} else if (element.getAttribute("class") !== value) {
				if (isHydrating) {
					emitHydrationWarning(
						name,
						quietProps,
						value,
						element.getAttribute("class"),
						element,
					);
				}
				element.setAttribute("class", value as string);
			}
			break;
		case "innerHTML":
			if (value !== oldValue) {
				if (isHydrating) {
					emitHydrationWarning(
						name,
						quietProps,
						value,
						element.innerHTML,
						element,
					);
				}
				element.innerHTML = value as any;
			}

			break;
		case "dangerouslySetInnerHTML": {
			const htmlValue =
				value && typeof value === "object" && "__html" in value
					? (value.__html ?? "")
					: "";
			const oldHtmlValue =
				oldValue && typeof oldValue === "object" && "__html" in oldValue
					? (oldValue.__html ?? "")
					: "";
			if (htmlValue !== oldHtmlValue) {
				element.innerHTML = htmlValue as any;
			}
			break;
		}
		case "htmlFor":
			if ("for" in props) break;
			if (value == null || value === false) {
				element.removeAttribute("for");
			} else {
				element.setAttribute("for", String(value === true ? "" : value));
			}
			break;
		default: {
			if (
				name[0] === "o" &&
				name[1] === "n" &&
				name[2] === name[2].toUpperCase() &&
				typeof value === "function"
			) {
				// Support React-style event names (onClick, onChange, etc.)
				name = name.toLowerCase();
			}

			// Support React-style SVG attribute names (strokeWidth, etc.)
			if (isSVG && name in REACT_SVG_PROPS) {
				name = REACT_SVG_PROPS[name];
			}

			// try to set the property directly
			if (
				name in element &&
				// boolean properties will coerce strings, but sometimes they map to
				// enumerated attributes, where truthy strings ("false", "no") map to
				// falsy properties, so we force using setAttribute.
				!(
					typeof value === "string" &&
					typeof (element as any)[name] === "boolean"
				) &&
				isWritableProperty(element, name)
			) {
				// For URL properties like src and href, the DOM property returns the
				// resolved absolute URL. We need to resolve the prop value the same way
				// to compare correctly.
				let domValue = (element as any)[name];
				let propValue = value;
				if (
					(name === "src" || name === "href") &&
					typeof value === "string" &&
					typeof domValue === "string"
				) {
					try {
						propValue = new URL(value, element.baseURI).href;
					} catch {
						// Invalid URL, use original value for comparison
					}
				}

				if (propValue !== domValue || oldValue === undefined) {
					if (
						isHydrating &&
						typeof (element as any)[name] === "string" &&
						(element as any)[name] !== value
					) {
						emitHydrationWarning(
							name,
							quietProps,
							value,
							(element as any)[name],
							element,
						);
					}
					// if the property is writable, assign it directly
					(element as any)[name] = value;
				}

				return;
			}

			if (value === true) {
				value = "";
			} else if (value == null || value === false) {
				if (isHydrating && element.hasAttribute(name)) {
					emitHydrationWarning(
						name,
						quietProps,
						value,
						element.getAttribute(name),
						element,
					);
				}

				element.removeAttribute(name);
				return;
			} else if (typeof value !== "string") {
				value = String(value);
			}

			if (element.getAttribute(name) !== value) {
				if (isHydrating) {
					emitHydrationWarning(
						name,
						quietProps,
						value,
						element.getAttribute(name),
						element,
					);
				}

				element.setAttribute(name, value as any);
			}
		}
	}
}

export const adapter: Partial<RenderAdapter<Node, string, Node>> = {
	scope({
		scope: xmlns,
		tag,
		props,
		root,
	}: {
		scope: string | undefined;
		tag: string | symbol;
		props: Record<string, any>;
		root: Node | undefined;
	}): string | undefined {
		switch (tag) {
			case Portal: {
				const ns = root instanceof Element ? root.namespaceURI : null;
				xmlns =
					ns === SVG_NAMESPACE
						? SVG_NAMESPACE
						: ns === MATHML_NAMESPACE
							? MATHML_NAMESPACE
							: undefined;
				break;
			}
			case "svg":
				xmlns = SVG_NAMESPACE;
				break;
			case "math":
				xmlns = MATHML_NAMESPACE;
				break;
			case "foreignObject":
				xmlns = undefined;
				break;
		}

		return props.xmlns || xmlns;
	},

	create({
		tag,
		tagName,
		scope: xmlns,
		root,
	}: {
		tag: string | symbol;
		tagName: string;
		scope: string | undefined;
		root: Node | undefined;
	}): Node {
		if (typeof tag !== "string") {
			throw new Error(`Unknown tag: ${tagName}`);
		} else if (tag.toLowerCase() === "svg") {
			xmlns = SVG_NAMESPACE;
		} else if (tag.toLowerCase() === "math") {
			xmlns = MATHML_NAMESPACE;
		} else if (tag === "foreignObject") {
			xmlns = SVG_NAMESPACE;
		}

		const doc = getRootDocument(root);
		return xmlns ? doc.createElementNS(xmlns, tag) : doc.createElement(tag);
	},

	adopt({
		tag,
		tagName,
		node,
		root,
	}: {
		tag: string | symbol;
		tagName: string;
		node: Node | undefined;
		root: Node | undefined;
	}): Array<Node> | undefined {
		if (typeof tag !== "string" && tag !== Portal) {
			throw new Error(`Unknown tag: ${tagName}`);
		}

		const doc = getRootDocument(root);
		if (
			node === doc.body ||
			node === doc.head ||
			node === doc.documentElement ||
			node === doc
		) {
			console.warn(
				`Hydrating ${node.nodeName.toLowerCase()} is discouraged as it is destructive and may remove unknown nodes.`,
			);
		}

		if (
			node == null ||
			(typeof tag === "string" &&
				(node.nodeType !== Node.ELEMENT_NODE ||
					tag.toLowerCase() !== (node as Element).tagName.toLowerCase()))
		) {
			console.warn(`Expected <${tagName}> while hydrating but found: `, node);
			return;
		}

		return Array.from(node.childNodes);
	},

	patch({
		tag,
		tagName,
		node,
		props,
		oldProps,
		scope: xmlns,
		copyProps,
		quietProps,
		isHydrating,
	}: {
		tag: string | symbol;
		node: Node;
		tagName: string;
		props: Record<string, any>;
		oldProps: Record<string, any> | undefined;
		scope: string | undefined;
		root: Node | undefined;
		copyProps: Set<string> | undefined;
		quietProps: Set<string> | undefined;
		isHydrating: boolean;
	}): void {
		if (node.nodeType !== Node.ELEMENT_NODE) {
			throw new TypeError(`Cannot patch node: ${String(node)}`);
		} else if (props.class && props.className) {
			console.error(
				`Both "class" and "className" set in props for <${tagName}>. Use one or the other.`,
			);
		}

		const element = node as Element;
		const isSVG = xmlns === SVG_NAMESPACE || tag === "foreignObject";
		const isMathML = xmlns === MATHML_NAMESPACE;
		// First pass: iterate oldProps to handle removals
		if (oldProps) {
			for (let name in oldProps) {
				if (name in props) continue;
				patchProp(
					element,
					name,
					undefined,
					oldProps[name],
					props,
					isSVG,
					isMathML,
					copyProps,
					quietProps,
					isHydrating,
				);
			}
		}
		// Second pass: iterate props to handle additions and updates
		for (let name in props) {
			patchProp(
				element,
				name,
				props[name],
				oldProps ? oldProps[name] : undefined,
				props,
				isSVG,
				isMathML,
				copyProps,
				quietProps,
				isHydrating,
			);
		}
	},

	arrange({
		tag,
		node,
		props,
		children,
	}: {
		tag: string | symbol;
		node: Node;
		props: Record<string, any>;
		children: Array<Node>;
		root: Node | undefined;
	}): void {
		if (tag === Portal && (node == null || typeof node.nodeType !== "number")) {
			throw new TypeError(
				`<Portal> root is not a node. Received: ${String(node)}`,
			);
		}

		if (!("innerHTML" in props) && !("dangerouslySetInnerHTML" in props)) {
			let oldChild = node.firstChild;
			for (let i = 0; i < children.length; i++) {
				const newChild = children[i];
				if (oldChild === newChild) {
					// the child is already in the right place, so we can skip it
					oldChild = oldChild.nextSibling;
				} else {
					node.insertBefore(newChild, oldChild);
					if (
						tag !== Portal &&
						oldChild &&
						i + 1 < children.length &&
						oldChild !== children[i + 1]
					) {
						oldChild = oldChild.nextSibling;
					}
				}
			}
		}
	},

	remove({
		node,
		parentNode,
		isNested,
	}: {
		node: Node;
		parentNode: Node;
		isNested: boolean;
		root: Node | undefined;
	}): void {
		if (!isNested && node.parentNode === parentNode) {
			parentNode.removeChild(node);
		}
	},

	text({
		value,
		oldNode,
		hydrationNodes,
		root,
	}: {
		value: string;
		hydrationNodes: Array<Node> | undefined;
		oldNode: Node | undefined;
		root: Node | undefined;
	}): Node {
		const doc = getRootDocument(root);
		if (hydrationNodes != null) {
			let node = hydrationNodes.shift();
			if (!node || node.nodeType !== Node.TEXT_NODE) {
				console.warn(`Expected "${value}" while hydrating but found:`, node);
			} else {
				// value is a text node, check if it matches the expected text
				const textData = (node as Text).data;
				if (textData.length > value.length) {
					if (textData.startsWith(value)) {
						// the text node is longer than the expected text, so we
						// reuse the existing text node, but truncate it and unshift the rest
						(node as Text).data = value;
						hydrationNodes.unshift(
							doc.createTextNode(textData.slice(value.length)),
						);

						return node;
					}
				} else if (textData === value) {
					return node;
				}

				// We log textData and not node because node will be mutated
				console.warn(
					`Expected "${value}" while hydrating but found:`,
					textData,
				);
				oldNode = node;
			}
		}

		if (oldNode != null) {
			if ((oldNode as Text).data !== value) {
				(oldNode as Text).data = value;
			}

			return oldNode;
		}

		return doc.createTextNode(value);
	},

	raw({
		value,
		scope: xmlns,
		hydrationNodes,
		root,
	}: {
		value: string | Node;
		scope: string | undefined;
		hydrationNodes: Array<Node> | undefined;
		root: Node | undefined;
	}): ElementValue<Node> {
		let nodes: Array<Node>;
		if (typeof value === "string") {
			const doc = getRootDocument(root);
			const el =
				xmlns == null
					? doc.createElement("div")
					: xmlns === SVG_NAMESPACE
						? doc.createElementNS(xmlns, "svg")
						: doc.createElementNS(xmlns, "math");
			el.innerHTML = value;
			nodes = Array.from(el.childNodes);
		} else {
			nodes = value == null ? [] : Array.isArray(value) ? [...value] : [value];
		}

		if (hydrationNodes != null) {
			for (let i = 0; i < nodes.length; i++) {
				const node = nodes[i];
				// check if node is equal to the next node in the hydration array
				const hydrationNode = hydrationNodes.shift();
				if (
					hydrationNode &&
					typeof hydrationNode === "object" &&
					typeof hydrationNode.nodeType === "number" &&
					node.isEqualNode(hydrationNode as Node)
				) {
					nodes[i] = hydrationNode as Node;
				} else {
					console.warn(
						`Expected <Raw value="${String(value)}"> while hydrating but found:`,
						hydrationNode,
					);
				}
			}
		}

		return nodes.length === 0
			? undefined
			: nodes.length === 1
				? nodes[0]
				: nodes;
	},
};

export class DOMRenderer extends Renderer<Node, string, Element> {
	constructor() {
		super(adapter);
	}

	render(
		children: Children,
		root: Element,
		ctx?: Context,
	): Promise<ElementValue<Node>> | ElementValue<Node> {
		validateRoot(root);
		return super.render(children, root, ctx);
	}

	hydrate(
		children: Children,
		root: Element,
		ctx?: Context,
	): Promise<ElementValue<Node>> | ElementValue<Node> {
		validateRoot(root);
		return super.hydrate(children, root, ctx);
	}
}

function validateRoot(root: unknown): asserts root is Element {
	if (
		root == null ||
		(typeof root === "object" && typeof (root as any).nodeType !== "number")
	) {
		throw new TypeError(`Render root is not a node. Received: ${String(root)}`);
	} else if ((root as Node).nodeType !== Node.ELEMENT_NODE) {
		throw new TypeError(
			`Render root must be an element node. Received: ${String(root)}`,
		);
	}
}

export const renderer = new DOMRenderer();

declare global {
	module Crank {
		interface EventMap extends GlobalEventHandlersEventMap {}
	}
}
